import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Container } from 'react-bootstrap'
import QueueCard from './QueueCard'
import { QueueColumnProps } from './types'
import './MonitorPage.scss'
import StatusCards from '../../components/StatusCards'
import { Indicators } from './Indicators'
import { useLiveSupportKanbanState } from '../MonitorPage/live-support-kanban-state.hooks'
import { auth, devFlag, liveSupport } from '../../clients/iron-fit'
import axios from 'axios'

const QueueColumn: React.FC<QueueColumnProps> = ({ title, count, data }) => {
  return (
    <div className="queue-column">
      <h6 className="queue-column-title">
        {title} ({count.toString().padStart(2, '0')})
      </h6>

      <div className="queue-column-cards">
        {data.map((cardData, index) => (
          <QueueCard key={cardData.userId ?? index} {...cardData} />
        ))}
      </div>
    </div>
  )
}

const MonitorPage: React.FC = () => {
  const [, userIdLoading] = auth.useAuthUserId()
  const [, supportsPendingSyncLoading] = liveSupport.useLiveSupportsSync({
      syncPending: devFlag ? true : undefined,
      first: devFlag ? undefined : 1,
  })
  const [patientStatus, setPatientStatus] = useState<any>('ONLINE')
  const [viewMode, setViewMode] = useState<'queue' | 'indicators'>('queue')
  const [corporations, setCorporations] = useState<any>()
  const [selectedCorporationIds, setSelectedCorporationIds] = useState<string[]>([])
  const [kanbanState, kanbanStateLoading] = useLiveSupportKanbanState(patientStatus, selectedCorporationIds)

  const loading = userIdLoading || kanbanStateLoading || supportsPendingSyncLoading
  const handleCorporationFilterChange = (selectedIds: string[]) => {
    setSelectedCorporationIds(selectedIds)
  }
  
  useEffect(() => {
    document.body.classList.add('monitor-page-active')
    return () => {
      document.body.classList.remove('monitor-page-active')
    }
  }, [])

  const handlePatientStatusChange = useCallback((status: 'ONLINE' | 'OFFLINE' | 'BOTH') => {
    setPatientStatus(status)
  }, [])

  const columnsData = useMemo(() => {
    return [
    {
      id: 'fila_operador',
      title: 'FILA PARA ENFERMAGEM',
      count: kanbanState.patientsAwaitingOperator?.length || 0,
      data: kanbanState.patientsAwaitingOperator || [],
    },
    {
      id: 'operadores_atendimento',
      title: 'ENFERMEIROS EM ATENDIMENTO',
      count: kanbanState.operators?.length || 0,
      data: kanbanState.operators || [],
    },
    {
      id: 'fila_clinico',
      title: 'FILA PARA CLÍNICO',
      count: kanbanState.patientsAwaitingDoctor?.length || 0,
      data: kanbanState.patientsAwaitingDoctor || [],
    },
    {
      id: 'clinico_atendimento',
      title: 'ATENDIMENTO CLÍNICO',
      count: kanbanState.doctors?.length || 0,
      data: kanbanState.doctors || [],
    },
    {
      id: 'fila_psicologo',
      title: 'FILA PARA PSICOLÓGICO',
      count: kanbanState.patientsAwaitingPsychologist?.length || 0,
      data: kanbanState.patientsAwaitingPsychologist || [],
    },
    {
      id: 'psicologo_atendimento',
      title: 'ATENDIMENTO PSICOLÓGICO',
      count: kanbanState.psychologists?.length || 0,
      data: kanbanState.psychologists || [],
    },
    {
      id: 'fila_assistant',
      title: 'FILA PARA AVALIAÇÃO DO ASSISTENTE',
      count: kanbanState.patientsAwaitingAssistant?.length || 0,
      data: kanbanState.patientsAwaitingAssistant || [],
    },
    {
      id: 'assistant_atendimento',
      title: 'ASSISTÊNCIA DE ACONSELHAMENTO',
      count: kanbanState.assistants?.length || 0,
      data: kanbanState.assistants || [],
    },
  ];
  }, [kanbanState])

  const staffCount = kanbanState.staffCount || 0

  const totalPatients = useMemo(() =>
    (kanbanState.patientsAwaitingOperator?.length || 0) +
    (kanbanState.patientsAwaitingDoctor?.length || 0) +
    (kanbanState.patientsAwaitingAssistant?.length || 0) +
    (kanbanState.patientsAwaitingPsychologist?.length || 0)
  , [kanbanState])

  useEffect(() => {
    getCorporationList()
  }, [])

  const getCorporationList = async () => {
    axios
      .get(`${process.env.REACT_APP_IRON_FIT_CORPORATION}doctor/getAllCorporation`)
      .then((res: any) => {
        const {
          data: { data },
        } = res
        setCorporations(data)
      })
      .catch((err: any) => console.log('err', err))
  }
  

  return (
    <Container fluid className="mt-4">
      <StatusCards
        doctorCount={staffCount}
        patientCount={totalPatients + kanbanState.attendingCount}
        waitingCount={totalPatients || 0}
        attendingCount={kanbanState.attendingCount || 0}
        returningCount={kanbanState.returningCount || 0}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        corporations={corporations}
        onCorporationFilterChange={handleCorporationFilterChange}
        patientStatus={patientStatus}
        onPatientStatusChange={handlePatientStatusChange}
      />
      {loading && <div className='text-black text-center mt-2 mb-5 d-block'>Loading...</div>}
      <div className="columns-scroll-container">
        {viewMode === 'queue' &&
          columnsData.map((column) => <QueueColumn key={column.id} title={column.title} count={column.count} data={column.data} />)}
          {viewMode === 'indicators' && <Indicators />}
      </div>
    </Container>
  )
}

export default MonitorPage
